import type { CurrentUser } from "@catmail/shared";
import { Cat, ImagePlus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Avatar, Button, FormDialog, TextField } from "@/components";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";
import {
  AVATAR_ACCEPT,
  AvatarImageProblem,
  toAvatarImage,
  type AvatarImageError,
} from "../../lib/avatarImage";
import { CatProblem, pickCat } from "../../lib/catApi";

/**
 * 프로필 사진을 고르는 창.
 *
 * **고르자마자 올리지 않는다.** 사진은 가운데를 정사각형으로 잘라서 저장되므로
 * (lib/avatarImage.ts) 고른 그림과 실제로 보일 그림이 다를 수 있다. 잘린
 * 결과를 먼저 보여주고 그다음에 바꾼다 — 여기 보이는 것이 곧 결과다.
 */

const PROBLEM_TEXT: Record<AvatarImageError, string> = {
  "not-image": "그림 파일만 올릴 수 있어요",
  "too-large": "사진이 너무 커요. 다른 사진을 골라주세요",
  "decode-failed": "이 사진을 열지 못했어요",
  "encode-failed": "사진을 바꾸지 못했어요. 다른 사진을 골라주세요",
};

type AvatarDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: CurrentUser;
  pending?: boolean;
  /** 서버가 거절한 사유. 없으면 안 그린다 */
  error?: string | null;
  onSubmit: (contentBase64: string) => void;
  /** 지금 사진을 지우고 이니셜로 돌아간다 */
  onRemove: () => void;
};

export function AvatarDialog({
  open,
  onOpenChange,
  user,
  pending = false,
  error,
  onSubmit,
  onRemove,
}: AvatarDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<{ base64: string; previewUrl: string } | null>(
    null,
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [catQuery, setCatQuery] = useState("");
  const [fetchingCat, setFetchingCat] = useState(false);

  // 창을 다시 열면 지난번에 고르던 것은 없던 일이다. 닫으면서 지우면 사라지는
  // 모습이 애니메이션에 그대로 보인다.
  useEffect(() => {
    if (open) {
      setPicked(null);
      setProblem(null);
      setCatQuery("");
    }
  }, [open]);

  async function take(source: Blob | undefined): Promise<void> {
    if (!source) return;
    setProblem(null);

    try {
      setPicked(await toAvatarImage(source));
    } catch (caught) {
      setPicked(null);
      setProblem(
        caught instanceof AvatarImageProblem
          ? PROBLEM_TEXT[caught.reason]
          : "사진을 읽지 못했어요",
      );
    }
  }

  /**
   * 고양이를 한 마리 데려온다.
   *
   * 검색어가 비면 아무 고양이나 온다. 같은 검색어로 다시 눌러도 다른 고양이가
   * 오므로(lib/catApi.ts) 마음에 들 때까지 누르면 된다.
   */
  async function bringCat(): Promise<void> {
    setProblem(null);
    setFetchingCat(true);

    try {
      await take(await pickCat(catQuery));
    } catch (caught) {
      setProblem(
        caught instanceof CatProblem ? caught.message : "고양이를 데려오지 못했어요",
      );
    } finally {
      setFetchingCat(false);
    }
  }

  const hint = problem ?? error;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="프로필 사진"
      description="같은 서버 사람에게는 이 사진이 보여요. 바깥으로 보내는 메일에는 실리지 않아요."
      submitLabel="바꾸기"
      pendingLabel="올리는 중…"
      pending={pending}
      canSubmit={picked !== null}
      onSubmit={() => picked && onSubmit(picked.base64)}
      hint={
        hint ? (
          <p className="text-danger text-[13px]" role="alert">
            {hint}
          </p>
        ) : (
          <p className="text-muted-3 text-[13px]">
            PNG · JPEG · WebP · GIF. 가운데를 정사각형으로 잘라서 올려요.
          </p>
        )
      }
    >
      <div className="flex items-center gap-4">
        <Avatar
          size="3xl"
          initials={user.initials}
          colorIndex={user.colorIndex}
          // 고른 것이 있으면 그것, 없으면 지금 쓰는 사진, 그것도 없으면 이니셜.
          imageUrl={picked?.previewUrl ?? user.avatarUrl}
          label={user.displayName}
        />

        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInput.current?.click()}
          >
            <ImagePlus className="size-3.5" />
            {picked ? "다른 사진 고르기" : "사진 고르기"}
          </Button>

          {/*
            올릴 사진이 없는 사람이 훨씬 많다. 이 앱 이름이 Cat Mail이니
            기본으로 내미는 그림도 고양이가 맞다.
          */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fetchingCat}
            onClick={() => void bringCat()}
          >
            <Cat className="size-3.5" />
            {fetchingCat ? "데려오는 중…" : "고양이 데려오기"}
          </Button>

          {/*
            사진이 없는 사람에게 "지우기"를 보여주면 누를 것이 없는 단추가 된다.
            고르는 중이면 아직 안 지운 것이므로 그때도 지금 사진 기준으로 낸다.
          */}
          {user.avatarUrl ? (
            <button
              type="button"
              onClick={onRemove}
              disabled={pending}
              className={`text-danger flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13px] disabled:opacity-45 ${PRESS_FLAT} ${FOCUS}`}
            >
              <Trash2 className="size-3.5" />
              지금 사진 지우기
            </button>
          ) : null}
        </div>
      </div>

      {/*
        어떤 고양이인지 말할 수 있다. 비워두면 아무 고양이나 온다.

        **Enter로 보내지 않는다.** 이 창의 form은 "바꾸기"라, 여기서 Enter를
        누르면 고양이를 데려오는 대신 아직 아무것도 안 고른 채로 저장을 누른
        셈이 된다.
      */}
      <TextField
        label="어떤 고양이"
        placeholder="어떤 고양이? (예: 까만, 잠자는)"
        value={catQuery}
        maxLength={40}
        onChange={(event) => setCatQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          void bringCat();
        }}
      />

      {/*
        파일 고르는 창은 브라우저가 띄운다. 생김새를 우리가 정할 수 없어서
        숨겨두고 위 단추로 연다 — 그래야 나머지 단추와 같은 모양이 된다.
      */}
      <input
        ref={fileInput}
        type="file"
        accept={AVATAR_ACCEPT}
        className="hidden"
        onChange={(event) => {
          void take(event.target.files?.[0]);
          // 같은 파일을 다시 고를 수 있어야 한다. 값이 남아 있으면 change가 안 뜬다.
          event.target.value = "";
        }}
      />
    </FormDialog>
  );
}
